import { DynamicSelectData } from "@/shared/components/DynamicSelect/model/DynamicSelectData";
import Snackbar from "@/shared/components/Snackbar/Snackbar";
import { FORM_MESSAGES } from "@/shared/constants/FormConstants";
import { STYLE_CLASSES } from "@/shared/constants/StyleConstants";
import { Utils } from "@/shared/utils/utils";
import store from "@/store";
import { Http } from "@status/codes";
import { AxiosError } from "axios";
import { cloneDeep } from "lodash";
import { Component, Inject, Prop, Vue, Watch } from "vue-property-decorator";
import { mapGetters } from "vuex";
import { DASHBOARD_TEMPLATE_EVENTS } from "../../constants/DashboardTemplatesConstants";
import { CollectionFilter } from "../../models/CollectionFilter";
import { DashboardTemplateResponse } from "../../models/DashboardTemplateResponse";
import { Field } from "../../models/Field";
import { FilterItemOfQuery } from "../../models/FilterItemOfQuery";
import {
  FilterField,
  FilterOfCollection
} from "../../models/FilterOfCollection";
import { IndicatorOrTag } from "../../models/IndicatorOrTag";
import { Layout } from "../../models/Layout";
import { PropertiesToChart } from "../../models/PropertiesToChart";
import { Query } from "../../models/Query";
import {
  CollectionsOptions,
  EventChart,
  SectionOfDashboard,
  SectionsToChart
} from "../../models/SectionOfDashboard";
import {
  DashboardSectionLayoutUpdate,
  PropertiesToChartLayoutUpdateChart,
  PropertiesToChartLayoutUpdateTag
} from "../../models/SectionOfDashboardUpdateLocation";
import { ViewsConfigResponse } from "../../models/viewsConfigResponse";
import { BqDashboardService } from "../../services/BqDashboardService";
import { buildAndExecuteQuery, buildFilters, prepareData } from "../../Utils";
import DraggableWrapper from "../DraggableWrapper/DraggableWrapper.vue";
import { getMaxWidthAndHeight } from "../DraggableWrapper/utils/getMaxWidthAndHeight";
import { normalizeLayout } from "../DraggableWrapper/utils/normalizeLayout";
import { resolveOverlaps } from "../DraggableWrapper/utils/resolveOverlaps";
import FiltersModal from "../FiltersModal/FiltersModal.vue";
import DashboardViewSection from "./DashboardViewSection/DashboardViewSection.vue";

@Component({
  name: "DashboardView",
  components: {
    DashboardViewSection,
    DraggableWrapper,
    FiltersModal
  },
  computed: {
    ...mapGetters({
      sectionsOfDashboardStore: "getSectionsOfdashboard",
      eventChart: "getEventChart"
    })
  }
})
export default class DashboardView extends Vue {
  @Inject() bqDashboardService!: BqDashboardService;

  @Prop() realDashboard?: boolean;
  @Prop() dashboardTemplate?: DashboardTemplateResponse;

  sectionsOfDashboardStore!: Array<SectionOfDashboard>;
  sectionsToChart: SectionsToChart[] = [];
  template: DashboardTemplateResponse | undefined;
  collectionsData: ViewsConfigResponse[] = [];
  primaryBtnClass = STYLE_CLASSES.PRIMARY_BTN;
  showFilter = true;
  loading = false;
  showLoadingBar = false;
  filtersOfChartSelected: FilterOfCollection[] = [];
  collectionDataOfChartSelected: ViewsConfigResponse = {
    id: "",
    name: "",
    fields: []
  };
  currentCollectionFilterIndex = -1;
  currentSectionFilterIndex = -1;
  waitingEventGraph = false;

  eventChart!: EventChart;

  modalFilterData: {
    title: string;
  } | null = { title: "" };
  roundBtnClass = STYLE_CLASSES.TERTIARY_ROUND_BTN;
  updateLocations: DashboardSectionLayoutUpdate[] = [];
  groupBy: DynamicSelectData[] = [];
  seeBy: DynamicSelectData[] = [];

  async created() {
    if (this.realDashboard) {
      this.showLoadingBar = true;
      try {
        await this.getCollectionsOptions();
        await this.renderRealDahsboard();
      } catch (e) {
        console.error(e);
      }
      await this.$nextTick();
      await this.$nextTick();
      await this.$nextTick();
      this.showFilter = false;
      this.showLoadingBar = false;
      return;
    }
    this.chart(this.sectionsOfDashboardStore);
  }

  MARGIN = -25;

  chart(sectionsWithData: Array<SectionOfDashboard>) {
    const BASE_WIDTH = window.innerWidth * 0.89;
    const BASE_HEIGHT = 400;

    let globalY = 0;
    const updateGlobalY = (y: number, height: number) => {
      const bottom = y + height;
      if (bottom > globalY) globalY = bottom;
    };
    const sectionsToChart = cloneDeep(
      sectionsWithData
        .map((section, indexSection) => {
          const collections = section.collections || [];
          const propertiesToChart: (PropertiesToChart | IndicatorOrTag)[] = [];
          let currentY = 0;
          const updateCurrentY = (y: number, height: number) => {
            const bottom = y + height + this.MARGIN;
            if (bottom > currentY) currentY = bottom;
          };
          for (const [indexCollection, collection] of collections.entries()) {
            if (!collection.groupBy?.length) continue;
            const layout = normalizeLayout(
              collection.layout,
              {
                y: currentY,
                width: BASE_WIDTH - this.MARGIN * 3,
                height: BASE_HEIGHT
              },
              BASE_WIDTH - this.MARGIN * 3
            );
            const chart: PropertiesToChart = {
              chartName: collection.chartName,
              indexSectionOfDashboard: indexSection,
              indexCollectionFilter: indexCollection,
              groupBy: collection.groupBy,
              chartType: collection.chartType,
              uuid: Utils.idGenerator(),
              type: "chart",
              chartColor: { hex: collection.chartColor },
              collection: collection.collection[0].id,
              layout
            } as PropertiesToChart;
            updateCurrentY(layout.y, layout.height);
            propertiesToChart.push(chart);
          }

          const addWithAutoLayout = (
            items: IndicatorOrTag[] | undefined,
            type: "indicator" | "tag"
          ) => {
            if (!items?.length) return;
            for (const item of items) {
              if (!item.show) continue;
              item.layout = normalizeLayout(
                item.layout,
                {
                  y: currentY
                },
                BASE_WIDTH - this.MARGIN * 3
              );
              item.uuid = Utils.idGenerator();
              item.type = type;
              updateCurrentY(item.layout.y, item.layout.height);
              propertiesToChart.push(item);
            }
          };

          addWithAutoLayout(section.tags, "tag");
          addWithAutoLayout(section.indicators, "indicator");
          if (propertiesToChart.length === 0) return null;
          //const resolved = resolveOverlaps(propertiesToChart, this.MARGIN);
          const { width, height } = getMaxWidthAndHeight(propertiesToChart);
          const minX = width + this.MARGIN * 3;
          const minY = height + this.MARGIN * 3;
          const maxSection = BASE_WIDTH - this.MARGIN;
          const layout: Layout = {
            x: section.layout?.x ?? 0,
            y: section.layout?.y ?? globalY,
            width:
              section.layout?.width && section.layout.width > minX
                ? section.layout.width > maxSection
                  ? maxSection
                  : section.layout.width
                : minX,
            height:
              section.layout?.height && section.layout.height > minY
                ? section.layout.height
                : minY
          };

          updateGlobalY(layout.y, layout.height);

          return {
            index: indexSection,
            uuid: Utils.idGenerator(),
            name: section.name,
            propertiesToChart: propertiesToChart,
            layout
          };
        })
        .filter(
          (section): section is NonNullable<typeof section> => section !== null
        )
    );
    this.sectionsToChart = sectionsToChart;
  }

  UpdateSectionsToChart(sectionsToChart: SectionsToChart[]) {
    if (this.realDashboard) {
      this.sectionsToChart = this.sectionsToChart.map(section => {
        return {
          ...section,
          propertiesToChart: section.propertiesToChart.map(property => {
            if (property.type === "chart") {
              const sectionFound = sectionsToChart.find(
                se => se.name === section.name
              );
              const propretyFound = sectionFound?.propertiesToChart.find(
                p => p.uuid === property.uuid
              ) as PropertiesToChart | undefined;
              if (propretyFound) {
                (property as PropertiesToChart).chartType =
                  propretyFound.chartType;
              }
            }

            return property;
          })
        };
      });
    } else {
      this.sectionsToChart = resolveOverlaps(sectionsToChart, this.MARGIN);
    }
    const mapProperty = (
      property: IndicatorOrTag | PropertiesToChart
    ):
      | PropertiesToChartLayoutUpdateChart
      | PropertiesToChartLayoutUpdateTag => {
      const layout = property.layout!;
      if (property.type === "indicator" || property.type === "tag") {
        return {
          name: (property as IndicatorOrTag).indicatorOrTagName,
          layout,
          type: property.type
        };
      }
      return {
        name: (property as PropertiesToChart).chartName ?? "",
        layout,
        type: property.type,
        chartType: (property as PropertiesToChart).chartType ?? "",
        collection: (property as PropertiesToChart).collection ?? ""
      } as PropertiesToChartLayoutUpdateChart;
    };
    this.updateLocations = sectionsToChart.map(
      (section): DashboardSectionLayoutUpdate => ({
        index: section.index,
        sectionName: section.name,
        layout: section.layout,
        propertiesToChart: section.propertiesToChart.map(mapProperty)
      })
    );
    store.commit("updateLocations", {
      updateLocations: this.updateLocations
    });
  }

  async getSection(indexSection: number, element: SectionOfDashboard) {
    try {
      element.collections.forEach((collection: any, indexCollection: any) => {
        this.getfieldsOfEveryCollection(indexSection, indexCollection);

        this.getData(
          collection.query as Query,
          indexSection,
          indexCollection,
          collection.chart.chartName
        );
      });
      const promiseTags = [];

      for (const [indexTag, tag] of element.tags.entries()) {
        promiseTags.push(
          this.queryTag(
            tag,
            indexSection,
            indexTag,
            tag.query!, // (element.collections || [])?.[0]?.query as Query,
            tag.view as string
          )
        );
      }
      await Promise.all(promiseTags);

      (element.indicators || []).forEach((indicator, indexIndicator) => {
        const result = this.resolveIndicator(
          indexSection,
          indicator.formulaToCreateIndicator
        );
        this.template!.sections[indexSection].indicators[
          indexIndicator
        ].value = Number(result.toFixed(2));
      });
    } catch (e) {
      console.error(e);
    }
  }

  @Watch("dashboardTemplate")
  async renderRealDahsboard() {
    this.template = cloneDeep(this.dashboardTemplate);
    const sections = this.template?.sections ? this.template?.sections : [];
    this.showLoadingBar = true;
    const sectionPromise = [];
    for (const [indexSection, element] of sections.entries()) {
      sectionPromise.push(this.getSection(indexSection, element));
    }
    await Promise.all(sectionPromise);
    this.showLoadingBar = false;
  }

  resolveIndicator(indexSection: number, expresion: any) {
    let resultado = null;
    let operador = null;

    const valores = this.template!.sections[indexSection].tags.map(e => {
      return { id: e.indicatorOrTagName, value: e.value };
    });

    for (const item of expresion) {
      const token = item.id;
      const valueTag = valores.find(e => e.id == token);
      if (valueTag) {
        if (resultado === null) {
          resultado = valueTag.value;
        } else if (operador) {
          resultado = this.operar(resultado, operador, valueTag.value);
          operador = null;
        }
      } else {
        operador = token;
      }
    }

    return resultado;
  }

  operar(a: any, operador: any, b: any) {
    switch (operador) {
      case "+":
        return a + b;
      case "-":
        return a - b;
      case "*":
        return a * b;
      case "/":
        return b !== 0 ? a / b : null;
      default:
        throw new Error(`Operador no soportado: ${operador}`);
    }
  }

  async queryTag(
    tag: IndicatorOrTag,
    indexSection: number,
    indexTag: number,
    query: Query,
    view: string
  ) {
    const selectorToOperate =
      tag.selectorFieldToOperate && tag.selectorFieldToOperate.length > 0
        ? tag.selectorFieldToOperate[0].id
        : "";
    query.selectFields.agregations = [
      {
        type: tag.operation as string,
        view: view,
        field: tag.field as string,
        alias: selectorToOperate ? selectorToOperate : (tag.field as string),
        selector: selectorToOperate
      }
    ];
    query.groupFields = [];
    const response = await this.getDataToChart(query);
    if (!Array.isArray(response.data) || response.data.length == 0) {
      console.error(response);
      return;
    }
    this.template!.sections[indexSection].tags[indexTag].value =
      response.data[0][tag.field as string];
    this.updateDataInStore();
    this.chart(this.sectionsOfDashboardStore);
  }

  getfieldsOfEveryCollection(indexSection: number, indexCollection: number) {
    this.template!.sections[indexSection].collectionsOptions = (cloneDeep(
      this.collectionsData
    ) as unknown) as CollectionsOptions[];
    this.template!.sections[indexSection].collections[
      indexCollection
    ].collection = [
      (this.collectionsData as [{ id: ""; name: ""; fields: [] }]).find(
        item =>
          item.id ==
          this.template!.sections[indexSection].collections[indexCollection].id
      ) as { id: string; name: string; fields: Field[] }
    ];
    this.template!.sections[indexSection].collections[
      indexCollection
    ].groupBy = this.fixGroupBy(
      (this.template!.sections[indexSection].collections[indexCollection]
        .collection[0].fields as unknown) as DynamicSelectData[],
      this.template!.sections[indexSection].collections[indexCollection].chart
        .groupBy
    );
    this.template!.sections[indexSection].collections[
      indexCollection
    ].seeBy = this.template!.sections[indexSection].collections[
      indexCollection
    ].chart.seeBy;
    this.template!.sections[indexSection].collections[
      indexCollection
    ].chartName = this.template!.sections[indexSection].collections[
      indexCollection
    ].chart.chartName;
    this.template!.sections[indexSection].collections[
      indexCollection
    ].chartData = {};
  }

  fixGroupBy(fields: DynamicSelectData[], groupBy: string[]) {
    return groupBy.map(groupField => {
      const field = fields.find(item => item.field === groupField);
      return {
        id: field?.field ?? groupField,
        name: field?.field ?? "",
        type: field?.type ?? ""
      };
    });
  }

  updateDataInStore() {
    store.commit(
      "updateSectionsOfDashboard",
      cloneDeep(this.template?.sections)
    );
  }
  async getDataToChart(query: any) {
    const response = await this.bqDashboardService.getDataToChart(query);
    console.info("data=", response.data);
    if (!response.data) {
      response.data = {
        xAxis: [],
        data: [],
        groups: {}
      };
    }
    return response;
  }

  async getData(
    query: Query,
    indexSection: number,
    indexCollection: number,
    chartName: string
  ) {
    this.loading = true;
    const response = await this.getDataToChart(query);
    this.loading = false;
    console.info("data=", response.data);

    const chartData = {
      chartName,
      xAxis: response.data.xAxisValues,
      data: response.data.series,
      groups: response.data.groups ?? []
    };
    this.template!.sections[indexSection].collections[
      indexCollection
    ].chartData = chartData;

    this.updateDataInStore();
    this.chart(this.sectionsOfDashboardStore);
  }

  async getCollectionsOptions() {
    try {
      const viewsConfig = await this.bqDashboardService.getViesConfig();

      if (viewsConfig.data) {
        this.collectionsData = viewsConfig.data;
        return;
      }
      Snackbar.popInfo(viewsConfig.message);
    } catch (error) {
      if ((error as AxiosError).response?.status == Http.Forbidden)
        Snackbar.popError(FORM_MESSAGES.FORBIDDEN_REQUEST);
      else Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }
  @Watch("eventChart")
  changeEventChart() {
    this.currentCollectionFilterIndex = this.eventChart.indexCollection;
    this.currentSectionFilterIndex = this.eventChart.indexSection;

    this.waitingEventGraph = true;
  }

  openFilter(indexSection: number, indexPropertie: number) {
    if (indexSection == undefined || indexPropertie == undefined) return;
    const element = this.sectionsToChart[indexSection].propertiesToChart[
      indexPropertie
    ];
    const section = this.template!.sections[indexSection];
    const indexCollection = section.collections.findIndex(
      collection =>
        collection.chartName == (element as PropertiesToChart).chartName
    );
    this.showFilter = true;
    this.currentCollectionFilterIndex = indexCollection;
    this.currentSectionFilterIndex = indexSection;
    this.filtersOfChartSelected =
      section.collections[indexCollection]?.filters ?? [];
    this.collectionDataOfChartSelected = this.collectionsData[indexCollection];
    this.modalFilterData = {
      title: section.name
    };
  }

  onChartTypeChanged(
    newChartType: string,
    indexSection: number,
    indexCollection: number
  ) {
    const cloneSectionsToChart = cloneDeep(this.sectionsToChart);
    (cloneSectionsToChart[indexSection].propertiesToChart[
      indexCollection
    ] as PropertiesToChart).chartType = newChartType;
    this.sectionsToChart = cloneSectionsToChart;
  }

  async makeQuery(
    indexSection: number,
    indexCollection: number,
    isSave: boolean
  ) {
    if (!isSave) {
      if (!this.waitingEventGraph) {
        return;
      }
      this.waitingEventGraph = false;
    }
    this.showFilter = false;
    this.showLoadingBar = true;
    try {
      let filters: FilterItemOfQuery[] = [];
      const collections =
        this.sectionsOfDashboardStore[indexSection].collections || [];
      filters = buildFilters(
        collections,
        filters,
        indexCollection,
        this.sectionsOfDashboardStore,
        indexSection,
        false
      );
      await Promise.all([
        this.buildEveryQuery(
          collections,
          filters,
          indexCollection,
          indexSection
        ),
        this.updateTagsQueries(
          this.sectionsOfDashboardStore[indexSection].tags,
          filters,
          indexSection
        )
      ]);
      this.chart(this.sectionsOfDashboardStore);
    } catch (e) {
      console.error(e);
    }

    this.showLoadingBar = false;
  }

  async buildEveryQuery(
    collections: CollectionFilter[],
    filters: FilterItemOfQuery[],
    indexCollectionFilter: number,
    indexSection: number
  ) {
    const promises = collections.map((collection, indexCollection) =>
      this.manageAsynQuerys(
        collection,
        filters,
        indexCollection,
        indexCollectionFilter,
        indexSection
      )
    );

    await Promise.all(promises);
  }

  async updateTagsQueries(
    tags: IndicatorOrTag[],
    filters: FilterItemOfQuery[],
    indexSection: number
  ) {
    try {
      for (const [index, tag] of tags.entries()) {
        const copyOfTag = cloneDeep(tag);

        if (!copyOfTag.query || Object.keys(copyOfTag.query).length === 0)
          continue;
        copyOfTag.query.filters = filters;

        const dataToChartResponse = await this.bqDashboardService.getDataToChart(
          copyOfTag.query
        );

        if (dataToChartResponse.code !== Http.Found) {
          continue;
        }

        copyOfTag.value = Number(
          (Object.values(dataToChartResponse.data[0])[0] as number).toFixed(2)
        );

        store.commit(DASHBOARD_TEMPLATE_EVENTS.UPDATE_TAG, {
          indexSectionOfDashboard: indexSection,
          index: index,
          tag: copyOfTag
        });
      }
    } catch (error) {
      // Error handling
    }
  }

  async manageAsynQuerys(
    collection: CollectionFilter,
    filters: FilterItemOfQuery[],
    indexCollection: number,
    indexCollectionFilter: number,
    indexSection: number
  ) {
    const groupBy = this.template!.sections[indexSection].collections[
      indexCollection
    ].groupBy as DynamicSelectData[];

    const seeBy = this.dashboardTemplate?.sections[indexSection]?.collections[
      indexCollection
    ].chart.seeBy;

    const rangeSize = this.dashboardTemplate?.sections[indexSection]
      ?.collections[indexCollection].chart.rangeSize;

    const query = buildAndExecuteQuery(
      collection,
      filters,
      indexCollection,
      indexCollectionFilter,
      groupBy,
      rangeSize,
      seeBy as DynamicSelectData[],
      true
    );
    if (!query || Object.keys(query).length === 0) return;

    store.commit(DASHBOARD_TEMPLATE_EVENTS.SET_QUERY, {
      indexSectionOfDashboard: indexSection,
      indexCollectionFilter: indexCollection,
      query: query
    });

    store.commit("setFetchData", true);
    const dataToChartResponse = await this.getDataToChart(query);
    store.commit("setFetchData", false);

    if (dataToChartResponse.code !== Http.Found) {
      /*  this.foundData = false;
      this.showCharts = false; */
      store.commit(DASHBOARD_TEMPLATE_EVENTS.SET_CONFIG_CHART, {
        notFound: true,
        indexSectionOfDashboard: indexSection,
        indexCollectionFilter: indexCollection
      });
    }

    this.sendDataToStoreReadyToChart(
      dataToChartResponse.data.xAxisValues,
      dataToChartResponse.data.series,
      dataToChartResponse.data.groups,
      indexCollection,
      groupBy,
      rangeSize,
      seeBy,
      indexSection,
      indexCollectionFilter
    );
  }

  sendDataToStoreReadyToChart(
    xAxis: [],
    data: [],
    groups: {},
    indexCollection: number,
    groupBy: DynamicSelectData[],
    rangeSize: number | null,
    seeBy: DynamicSelectData[],
    indexSection: number,
    indexCollectionFilter: number
  ) {
    const chartName = this.dashboardTemplate?.sections[indexSection]
      ?.collections[indexCollection].chart.chartName;
    const chartColor = this.dashboardTemplate?.sections[indexSection]
      ?.collections[indexCollection].chartColor;

    const prepareDataToStore = prepareData(
      chartName as string,
      xAxis,
      data,
      groups,
      indexCollection,
      groupBy,
      rangeSize,
      seeBy,
      indexSection,
      chartColor as { hex: "" },
      indexCollectionFilter
    );

    store.commit(
      DASHBOARD_TEMPLATE_EVENTS.SET_CONFIG_CHART,
      prepareDataToStore
    );
  }

  async onChangeFilters() {
    this.showLoadingBar = true;
    this.dashboardTemplate?.sections!.forEach((element, indexSection) => {
      let lastQuery: Query;
      element.collections.forEach((collection, indexCollection) => {
        const collectionData = this.getCollectionDataByIndexCollection(
          indexSection,
          indexCollection
        );
        const filtersCloned = cloneDeep(
          this.sectionsOfDashboardStore[indexSection].collections[
            indexCollection
          ].filters
        );
        const filters = (filtersCloned.map(e => {
          return {
            field: (e.field[0] as FilterField).id,
            operator: (e.operator[0] as any).id,
            value: e.value.map((e: any) => e.id),
            view: collection.id
          };
        }) as unknown) as FilterItemOfQuery[];
        collectionData.query = {
          ...collection.query,
          filters
        } as Query;

        this.getData(
          collectionData.query,
          indexSection,
          indexCollection,
          collectionData.chartName
        );
        if (indexCollection == (element.collections || []).length - 1) {
          lastQuery = collectionData.query;
        }
      });

      (element.tags || []).forEach((tag, indexTag) => {
        this.queryTag(
          tag,
          indexSection,
          indexTag,
          lastQuery,
          tag.view as string
        );
      });
    });
    this.showLoadingBar = false;
  }

  getCollectionDataByIndexCollection(
    indexSection: number,
    indexCollection: number
  ) {
    const collection = this.dashboardTemplate?.sections[indexSection]
      .collections[indexCollection];
    return {
      query: collection?.query as Query,
      chartName: collection?.chartName as string
    };
  }

  onPropertiesToChartChanged(
    newPropertiesToChart: (PropertiesToChart | IndicatorOrTag)[],
    indexSection: number
  ) {
    const cloneSectionsToChart = cloneDeep(this.sectionsToChart);
    cloneSectionsToChart[indexSection].propertiesToChart = newPropertiesToChart;
    this.UpdateSectionsToChart(cloneSectionsToChart);
  }
}
